/**
 * Uebersetzt eine Ratenzeile (`OrderPayment`, camelCase, teils Zeichenketten) in die
 * snake_case-Spalten von `order_payment_schedule` (`RATE_FELDER`,
 * `backend/src/domaene/schreibbareFelder.ts:141-151`).
 *
 * `due` (absolutes Datum einer lokalen Eingabe, unter anderem aus dem verbleibenden Pi-Term-Parser)
 * geht nach `due_date`; `dueDays` (relative Tagesangabe einer editierbaren Ratenzeile) geht nach
 * `due_days`. Die Funktion uebersetzt beide Formen unabhaengig voneinander; Task 13 holt die nach
 * dem Setzen gueltigen Raten als Server-Snapshot ueber die Zahlungsziel-Route.
 *
 * Namens-Mismatch zwischen lokal berechneten und vom Server geladenen Zeilen (dokumentiert unter
 * "Punkt 14", `docs/superpowers/decisions/2026-08-11-felder-ohne-endpunkt.md`): lokale Eingaben
 * tragen gegebenenfalls `due`/`paidDate`, vom Server
 * geladene Zeilen (`orders.ts:262-266`, `oeffentlich()`) tragen `dueDate`/`paidAt`. Beide Formen
 * sind legitime `OrderPayment`-Instanzen, also liest diese Funktion `due`/`paidDate` bevorzugt und
 * faellt auf `dueDate`/`paidAt` zurueck, statt bei Server-Zeilen still `null` zu uebersetzen.
 *
 * Leere Zeichenkette ist bei Zahlenspalten ("pct", "amount", "usd_rate") KEIN gueltiger Postgres-
 * numeric-Wert - `zahlAlsSpalte` uebersetzt sie nach `null`, nicht nach `0` (ein Betrag von 0 ist ein
 * anderer, gueltiger Wert als "noch nicht gepflegt").
 */
import type { OrderPayment } from "./types.ts";

function zahlAlsSpalte(wert: unknown): number | null {
  if (wert === undefined || wert === null || wert === "") return null;
  const zahl = Number(wert);
  return Number.isFinite(zahl) ? zahl : null;
}

function textAlsSpalte(wert: unknown): string | null {
  if (wert === undefined || wert === null) return null;
  const text = String(wert).trim();
  return text === "" ? null : text;
}

/**
 * Seit Plan 2026-10-04 (Ist-Zahlung) ist `paid_at` nicht mehr schreibbar und der Zahlstatus `paid` kein
 * Wert des Raten-PATCH/POST mehr: ein Wechsel von oder nach `paid` laeuft nur ueber die Ist-Zahlung
 * (`api/zahlungIst.ts`), der Server antwortet sonst mit 409. Darum steht `paid_at` hier nie mehr, und
 * `status` faehrt nie mit, wenn alter oder neuer Wert `paid` ist (`planeRatenSynchronisierung`).
 */
export function ratenFelderAusZeile(payment: OrderPayment): Record<string, unknown> {
  return {
    label: textAlsSpalte(payment.label),
    pct: zahlAlsSpalte(payment.pct),
    amount: zahlAlsSpalte(payment.amount),
    // `payment.due || dueDate` statt `??`: `normalizePaymentRow` (`lib/payments.ts:207`) setzt
    // `row.due = ""` UNBEDINGT, wenn `due` falsy ist - eine Zeile, die durch `bestellungFuerAnzeige`
    // (Drawer-Weg) gelaufen ist, traegt danach IMMER `due: ""`, nie `undefined`/`null`. `??` faellt
    // dort nie auf `dueDate` zurueck (Befund 1 der Gesamtpruefung, 2026-09-01). `||` behandelt eine
    // leere Zeichenkette wie fehlend - konsistent mit `textAlsSpalte`/`zahlAlsSpalte` oben.
    due_date: textAlsSpalte(payment.due || (payment as { dueDate?: unknown }).dueDate),
    due_days: zahlAlsSpalte((payment as { dueDays?: unknown }).dueDays),
    anchor: textAlsSpalte(payment.anchor),
    status: textAlsSpalte(payment.status) ?? "open",
    usd_rate: zahlAlsSpalte(payment.usdRate),
  };
}

export interface RatenSyncPlan {
  anzulegen: Array<{ felder: Record<string, unknown> }>;
  zuAendern: Array<{ payId: string; rowVersion: number; felder: Record<string, unknown> }>;
  zuLoeschen: Array<{ payId: string; rowVersion: number }>;
}

/**
 * Eine "alte" Zeile gilt nur dann als tatsaechlich vom Server geladen, wenn sie NEBEN `id` auch eine
 * gueltige `rowVersion` traegt (Review-Befund, Important, nach der ersten Fassung dieser Funktion).
 *
 * `ZahlungTab.tsx#zahlungsartSetzen`/`#inRatenUmwandeln` bauen den "alten" Satz aus der FUER DIE
 * ANZEIGE normalisierten Bestellung (`bestellungFuerAnzeige`). `normalizeOrderPayments`
 * (`lib/payments.ts`) fuellt eine zu kurze `payments`-Liste (der Normalfall bei jeder unberuehrten
 * Bestellung: `payments: []`) mit `createPayment()` auf - diese PHANTOMZEILEN bekommen eine LOKALE
 * `id` (aus `uid()`), aber KEIN `rowVersion`, weil sie am Server nie existiert haben. Ohne diese
 * Absicherung haette `alteNachId` sie als "bestehend" gefuehrt: verschwindet eine solche Zeile im
 * neuen Satz (z. B. weil `zahlungsartSetzen` den Ratensatz komplett ersetzt), landete sie in
 * `zuLoeschen` mit `rowVersion: Number(undefined)` = `NaN` - ein DELETE, das die Backend-Route mit
 * 400 ablehnt ("rowVersion (ganze Zahl) ist erforderlich."), NACHDEM die neuen Zeilen schon per POST
 * angelegt wurden (`anzulegen` laeuft vor `zuLoeschen`) - die Kette bricht ab, ein zweiter Versuch
 * legt den Ratensatz ein zweites Mal an.
 *
 * Eine Zeile ohne gueltige `rowVersion` faellt jetzt automatisch in den bereits vorhandenen Zweig
 * "id vorhanden, aber dem Sync unbekannt" (wird als `anzulegen` behandelt, ohne die lokale id an ein
 * Backend-INSERT zu haengen) bzw. taucht gar nicht erst in `zuLoeschen` auf.
 */
function istServerZeile(
  zeile: OrderPayment,
): zeile is OrderPayment & { id: string | number; rowVersion: number | string } {
  return !!zeile.id && Number.isInteger(Number(zeile.rowVersion));
}

/**
 * Vergleicht zwei Ratensaetze und plant die minimale Menge an POST/PATCH/DELETE-Aufrufen, um vom
 * alten zum neuen Satz zu kommen - ZahlungTab.tsx kennt heute nur "die ganze Liste ersetzen", das
 * Backend nur Operationen JE ZEILE (siehe Kopfkommentar `api/zahlungsraten.ts`). Eine Zeile mit `id`
 * UND gueltiger `rowVersion` im alten Satz, die im neuen fehlt, wird geloescht (siehe
 * `istServerZeile`); eine Zeile ohne `id` im neuen Satz wird angelegt; eine Zeile mit `id` in BEIDEN
 * Saetzen wird nur geaendert, wenn sich ihre uebersetzten Spalten unterscheiden - sonst entstuende
 * bei jedem Aufruf ein unnoetiger PATCH samt Versionssprung.
 */
export function planeRatenSynchronisierung(
  alte: readonly OrderPayment[],
  neue: readonly OrderPayment[],
): RatenSyncPlan {
  const alteNachId = new Map(
    alte.filter(istServerZeile).map((zeile) => [String(zeile.id), zeile]),
  );
  const neueIds = new Set(neue.filter((zeile) => zeile.id).map((zeile) => String(zeile.id)));

  const anzulegen: RatenSyncPlan["anzulegen"] = [];
  const zuAendern: RatenSyncPlan["zuAendern"] = [];

  neue.forEach((zeile, index) => {
    const felder: Record<string, unknown> = { ...ratenFelderAusZeile(zeile), sort_index: index };
    if (!zeile.id) {
      if (felder.status === "paid") delete felder.status;
      anzulegen.push({ felder });
      return;
    }
    const alteZeile = alteNachId.get(String(zeile.id));
    if (!alteZeile) {
      // Hat eine id, ist dem Sync aber unbekannt (sollte in der Praxis nicht vorkommen) - als
      // neue Zeile behandeln, ohne die fremde id an ein Backend-INSERT zu haengen.
      if (felder.status === "paid") delete felder.status;
      anzulegen.push({ felder });
      return;
    }
    const alteFelder: Record<string, unknown> = {
      ...ratenFelderAusZeile(alteZeile),
      sort_index: alte.indexOf(alteZeile),
    };
    const geaendert: Record<string, unknown> = {};
    for (const [spalte, wert] of Object.entries(felder)) {
      if (alteFelder[spalte] !== wert) geaendert[spalte] = wert;
    }
    // Wechsel von/nach `paid` gehoert der Ist-Zahlung, nie dem Raten-PATCH.
    if (alteFelder.status === "paid" || felder.status === "paid") delete geaendert.status;
    if (Object.keys(geaendert).length > 0) {
      zuAendern.push({
        payId: String(zeile.id),
        rowVersion: Number(alteZeile.rowVersion),
        felder: geaendert,
      });
    }
  });

  const zuLoeschen: RatenSyncPlan["zuLoeschen"] = alte
    .filter((zeile) => istServerZeile(zeile) && !neueIds.has(String(zeile.id)))
    .map((zeile) => ({ payId: String(zeile.id), rowVersion: Number(zeile.rowVersion) }));

  return { anzulegen, zuAendern, zuLoeschen };
}
